import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { signOut } from 'next-auth/react';
import React, { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Button, { ButtonVariant } from '@/components/molecules/button/button';
import ContentSearchBar from '@/components/organisms/content-search-bar/content-search-bar';
import variables from '@/components/organisms/main-navigation/main-navigation.variables';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageTransition } from '@/libraries/hooks/use-page-transition';
import { MainNavigationMenuItem } from '../../main-navigation';
import styles from './main-navigation-small-dropdown.module.scss';

export interface MainNavigationSmallDropdownProps {
  /** The class name to apply */
  className?: string;
  /** The link to use for content search */
  contentSearchPageLink?: Content.Properties.Link | null;
  /** Indicates whether the profile menu items are visible */
  hasProfileMenuItems?: boolean;
  /** Indicates whether the dropdown should have search bar */
  hasSearch?: boolean;
  /** Indicates whether the dropdown is open */
  isOpen?: boolean;
  /** the links to show */
  items?: MainNavigationMenuItem[];
  /** An event that is triggered on search */
  onSearch?: () => void;
  /** An array of profile menu items */
  profileMenuItems?: Content.Properties.ProfileMenuItem[];
}

const MainNavigationSmallDropdown = ({
  className,
  contentSearchPageLink,
  hasProfileMenuItems = false,
  hasSearch = false,
  isOpen = false,
  items = [],
  onSearch,
  profileMenuItems = [],
}: MainNavigationSmallDropdownProps) => {
  const { t } = useI18n();
  const [activeItem, setActiveItem] = useState<MainNavigationMenuItem>();
  const [activeSubItem, setActiveSubItem] = useState<MainNavigationMenuItem>();
  const [isSearchActive, setIsSearchActive] = useState(false);

  const showItems = items && !isSearchActive;

  const handleClickBack = () => {
    if (activeSubItem) {
      setActiveSubItem(undefined);

      return;
    }

    if (activeItem) {
      setActiveItem(undefined);
    }
  };

  const handleClickItem = (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    item?: MainNavigationMenuItem,
  ) => {
    if (!item?.items || !item.items.length) return;

    event?.preventDefault();

    setActiveItem(item);
  };

  const handleClickSubItem = (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    item?: MainNavigationMenuItem,
  ) => {
    if (!item?.items || !item.items.length) return;

    event?.preventDefault();

    setActiveSubItem(item);
  };

  const handleFocusSearch = (value: boolean) => {
    setIsSearchActive(value);
  };

  const handleProfileMenuItemClick =
    (item: Content.Properties.ProfileMenuItem) =>
    async (event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (item.type !== 'logout') return;

      event?.preventDefault();

      await signOut({ callbackUrl: '/' });
    };

  usePageTransition({
    onEnter: () => {
      setActiveItem(undefined);
      setActiveSubItem(undefined);
    },
  });

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasSearch]: hasSearch,
        [styles.isOpen]: isOpen,
        [styles.isSearchActive]: isSearchActive,
      })}
    >
      {hasSearch && (
        <div className={styles.contentSearch}>
          <ContentSearchBar
            link={contentSearchPageLink}
            onChangeFocus={value => handleFocusSearch(value)}
            onSearch={() => {
              onSearch && onSearch();
              setIsSearchActive(false);
            }}
            shouldRedirect
          />
        </div>
      )}

      {showItems && (
        <AnimatePresence>
          <motion.div
            key="main-navigation-small-dropdown"
            animate="show"
            exit="hidden"
            initial="hidden"
            transition={{ delay: 0.4, duration: 0.2 }}
            variants={{
              hidden: {
                opacity: 0,
              },
              show: {
                opacity: 1,
              },
            }}
          >
            {items.map((item, itemIndex) => (
              <ul
                key={itemIndex}
                className={classNames(styles.section, {
                  [styles.isActive]: !activeItem || activeItem === item,
                  [styles.isFeatured]: item.isFeatured,
                })}
              >
                <li className={styles.sectionItem}>
                  {activeItem && (
                    <AnchorLink
                      className={styles.backLink}
                      icon="ChevronLeft"
                      onClick={handleClickBack}
                      text={t('items.common.words.back')}
                    />
                  )}

                  <Link
                    className={classNames(styles.sectionHeader, {
                      [styles.isActive]:
                        !activeItem || (activeItem === item && !activeSubItem),
                    })}
                    onClick={
                      !activeItem && item.items && item.items.length > 0
                        ? event => event && handleClickItem(event, item)
                        : undefined
                    }
                    url={item.link.url}
                  >
                    <div className={styles.sectionHeaderContent}>
                      <Typography
                        className={styles.sectionHeaderText}
                        tagStyle={
                          activeItem ? 'displayMedium' : 'bodyLargeBold'
                        }
                      >
                        {item.link.text}
                      </Typography>
                    </div>

                    {item.items && item.items.length > 0 && (
                      <Icon
                        className={styles.sectionHeaderIcon}
                        name="ChevronRight"
                        size={16}
                      />
                    )}
                  </Link>

                  {item.items && item.items.length > 0 && (
                    <ul
                      className={classNames(styles.section, {
                        [styles.isActive]: activeItem === item,
                      })}
                    >
                      {item.items.map((subItem, subItemIndex) => (
                        <li key={subItemIndex} className={styles.sectionItem}>
                          <Link
                            className={classNames(styles.sectionHeader, {
                              [styles.isActive]:
                                !activeSubItem || activeSubItem === subItem,
                            })}
                            onClick={
                              !activeSubItem &&
                              subItem.items &&
                              subItem.items.length > 0
                                ? event =>
                                    event && handleClickSubItem(event, subItem)
                                : undefined
                            }
                            url={subItem.link.url}
                          >
                            <div className={styles.sectionHeaderContent}>
                              <Typography
                                className={styles.sectionHeaderText}
                                tagStyle={
                                  activeSubItem
                                    ? 'displayMedium'
                                    : 'bodyLargeBold'
                                }
                              >
                                {subItem.link.text}
                              </Typography>

                              {subItem.description && (
                                <Typography tagStyle="bodyMedium">
                                  {subItem.description}
                                </Typography>
                              )}
                            </div>

                            {subItem.items && subItem.items.length > 0 && (
                              <Icon
                                className={styles.sectionHeaderIcon}
                                name="ChevronRight"
                                size={16}
                              />
                            )}
                          </Link>

                          {subItem.items && subItem.items.length > 0 && (
                            <ul
                              className={classNames(styles.section, {
                                [styles.isActive]: activeSubItem === subItem,
                              })}
                            >
                              {subItem.items.map(
                                (subSubItem, subSubItemIndex) => (
                                  <li
                                    key={subSubItemIndex}
                                    className={styles.sectionItem}
                                  >
                                    <Link
                                      className={classNames(
                                        styles.sectionHeader,
                                        {
                                          [styles.isActive]:
                                            activeSubItem === subItem,
                                        },
                                      )}
                                      url={subSubItem.link.url}
                                    >
                                      <div
                                        className={styles.sectionHeaderContent}
                                      >
                                        <Typography
                                          className={styles.sectionHeaderText}
                                          tagStyle="bodyLargeBold"
                                        >
                                          {subSubItem.link.text}
                                        </Typography>

                                        {subSubItem.description && (
                                          <Typography tagStyle="bodyMedium">
                                            {subSubItem.description}
                                          </Typography>
                                        )}
                                      </div>
                                    </Link>
                                  </li>
                                ),
                              )}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              </ul>
            ))}
          </motion.div>
        </AnimatePresence>
      )}

      {hasProfileMenuItems &&
        profileMenuItems &&
        profileMenuItems?.length > 0 && (
          <div className={styles.buttons}>
            {profileMenuItems.map((item, itemIndex) => (
              <Button
                key={itemIndex}
                icon={item.icon}
                onClick={handleProfileMenuItemClick(item)}
                text={item.link.text}
                url={item.link.url}
                variant={
                  variables.mainNavigationSmallProfileMenuButtonVariant as ButtonVariant
                }
              />
            ))}
          </div>
        )}
    </div>
  );
};

MainNavigationSmallDropdown.displayName = 'MainNavigationSmallDropdown';

export default MainNavigationSmallDropdown;
